import {createRef, useState} from 'react';
import {Box, Avatar, Fab, FormLabel, Alert} from '@mui/material';
import {Person, Business} from '@mui/icons-material';
import {FiEdit, FiTrash2} from 'react-icons/fi';
import {BsInfoCircle} from 'react-icons/bs';
import {ImageCropperDialog} from '../image-cropper-dialog/image-cropper-dialog';
import {avatarSizesAndCoefficients} from '../../utils/constants/avatar-sizes-and-coefficients';

/**
 * Edit picture field.
 */
export function EditPicture(props: {
  type: 'user' | 'entity';
  label?: string;
  alt: string;
  useFileCropper: boolean;
  handleChange: (image?: {base64: string; file: File}) => void;
  src?: string;
  aspect?: number;
  size?: string;
  disabled?: boolean;
  className?: string;
  alertMessage?: string;
  accept?: string;
  clearable?: boolean;
  handleBlur?: () => void;
}) {
  // Hold state
  const [imageCropperFile, setImageCropperFile] = useState<File | undefined>();

  // Hold ref
  const imageInputFileRef = createRef<any>();

  // Open cropper
  const handleImageFileChange = (event: any) => {
    // Evaluate change
    if (event.target.files && event.target.files[0]) {
      if (props.useFileCropper) {
        // Set the image cropper file
        setImageCropperFile(event.target.files[0]);
      } else {
        // Initialize a new File Reader
        const reader = new FileReader();

        // On reader load
        reader.onload = () => {
          // Update the profile picture
          props.handleChange({base64: reader.result as string, file: event.target.files[0]});
        };

        // Launch reading process
        reader.readAsDataURL(event.target.files[0]);
      }
    }
  };

  // Return the cropped image
  const handleImageCropperClosed = (file?: File) => {
    // Close the image cropper
    setImageCropperFile(undefined);

    // If a file is available
    if (file) {
      // Initialize a new File Reader
      const reader = new FileReader();

      // On reader load
      reader.onload = () => {
        // Update the profile picture
        props.handleChange({base64: reader.result as string, file: file});
      };

      // Launch reading process
      reader.readAsDataURL(file);
    }
  };

  // Remove image
  const handleRemoveImage = () => {
    // Clear
    props.handleChange(undefined);
  };

  return (
    <>
      <Box className={props.className}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {props.label && (
            <FormLabel
              component="legend"
              disabled={props.disabled === true ? true : false}
              sx={{
                marginBottom: '12px',
                fontSize: '16px',
                fontWeight: 500,
                color: 'var(--dcm-gray)',
              }}
            >
              {props.label}
            </FormLabel>
          )}
          <Box
            sx={{
              position: 'relative',
              width: 'fit-content',
            }}
          >
            <Avatar
              alt={props.alt}
              src={props.src}
              sx={{
                width: `${avatarSizesAndCoefficients.avatarSizes.extraGiant}px`,
                height: `${avatarSizesAndCoefficients.avatarSizes.extraGiant}px`,
                borderRadius: `${
                  avatarSizesAndCoefficients.avatarSizes.extraGiant *
                  avatarSizesAndCoefficients.borderRadiusCoefficients.circle
                }px`,
                margin: 'auto',
                boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.1)',
              }}
            >
              {props.type === 'user' ? (
                <Person
                  sx={{
                    fontSize:
                      avatarSizesAndCoefficients.avatarSizes.extraGiant *
                      avatarSizesAndCoefficients.iconSizeCoefficient,
                  }}
                />
              ) : (
                <Business
                  sx={{
                    fontSize:
                      avatarSizesAndCoefficients.avatarSizes.extraGiant *
                      avatarSizesAndCoefficients.iconSizeCoefficient,
                  }}
                />
              )}
            </Avatar>
            <input
              ref={imageInputFileRef}
              style={{display: 'none'}}
              accept={props.accept || 'image/jpeg,image/avif'}
              type="file"
              onChange={handleImageFileChange}
              value=""
            />
            <Fab
              size="small"
              color="primary"
              disabled={props.disabled === true ? true : false}
              onClick={() => {
                imageInputFileRef.current.click();

                // Trigger blur
                if (props.handleBlur) {
                  props.handleBlur();
                }
              }}
              sx={{
                zIndex: 1,
                position: 'absolute',
                bottom: props.type === 'user' ? 0 : -10,
                right: props.type === 'user' ? 0 : -10,
                padding: '8px',
              }}
            >
              <FiEdit fontSize="24px" />
            </Fab>
            {props.src && props.clearable === true && (
              <Fab
                size="small"
                color="primary"
                disabled={props.disabled === true ? true : false}
                onClick={handleRemoveImage}
                sx={{
                  zIndex: 1,
                  position: 'absolute',
                  bottom: props.type === 'user' ? 0 : -10,
                  left: props.type === 'user' ? 0 : -10,
                  padding: '8px',
                  // Override with error color because it's not in the color prop
                  backgroundColor: 'var( --dcm-red-base)',
                  '&:hover': {
                    backgroundColor: 'var( --dcm-red-base)',
                    filter: 'brightness(95%)',
                  },
                }}
              >
                <FiTrash2 fontSize="24px" />
              </Fab>
            )}
          </Box>
        </Box>
        {props.alertMessage && (
          <Box
            sx={{
              marginTop: props.type === 'user' ? '12px' : '18px',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {/* TODO: Clarify a better pattern with Amanda */}
            <Alert
              severity="info"
              icon={<BsInfoCircle fontSize="16px" />}
              sx={{
                borderRadius: '5px',
                border: 0,
                width: '297px',
              }}
            >
              {props.alertMessage}
            </Alert>
          </Box>
        )}
      </Box>
      {imageCropperFile && (
        <ImageCropperDialog
          open={true}
          imageFile={imageCropperFile}
          handleClose={handleImageCropperClosed}
          size={props.size}
          aspect={props.aspect}
        />
      )}
    </>
  );
}
